import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import DictationInput from '../components/DictationInput';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  BookOpen, Plus, ExternalLink, Home, Briefcase, Heart, Phone, Search,
  UtensilsCrossed, Shirt, Car, Scale, GraduationCap, Users, MapPin, Star, Bookmark
} from 'lucide-react';

const CATEGORIES = [
  { value: 'food', label: 'Food Assistance', icon: UtensilsCrossed, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { value: 'clothing', label: 'Clothing', icon: Shirt, color: 'bg-purple-50 text-purple-700 border-purple-200' },
  { value: 'housing', label: 'Housing', icon: Home, color: 'bg-sky-50 text-sky-700 border-sky-200' },
  { value: 'recovery', label: 'Recovery Support', icon: Heart, color: 'bg-teal-50 text-teal-700 border-teal-200' },
  { value: 'transportation', label: 'Transportation', icon: Car, color: 'bg-amber-50 text-amber-700 border-amber-200' },
  { value: 'employment', label: 'Employment', icon: Briefcase, color: 'bg-blue-50 text-blue-700 border-blue-200' },
  { value: 'legal', label: 'Legal Support', icon: Scale, color: 'bg-slate-50 text-slate-700 border-slate-200' },
  { value: 'education', label: 'Education & Training', icon: GraduationCap, color: 'bg-orange-50 text-orange-700 border-orange-200' },
  { value: 'family', label: 'Family Services', icon: Users, color: 'bg-rose-50 text-rose-700 border-rose-200' },
  { value: 'crisis', label: 'Crisis Support', icon: Phone, color: 'bg-red-50 text-red-700 border-red-200' },
  { value: 'general', label: 'General', icon: BookOpen, color: 'bg-gray-50 text-gray-700 border-gray-200' },
];

const getCategoryConfig = (cat) => CATEGORIES.find(c => c.value === cat) || CATEGORIES[CATEGORIES.length - 1];

export default function ResourcesPage() {
  const { user, axiosAuth } = useAuth();
  const [resources, setResources] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [savedIds, setSavedIds] = useState(new Set());
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', category: 'recovery', description: '', url: '', location: '', phone: '', eligibility: '' });
  const [activeTab, setActiveTab] = useState('resources');
  const api = axiosAuth();
  const canCreate = ['coordinator', 'admin', 'peer_specialist', 'resource_partner'].includes(user.role);

  const load = async () => {
    try {
      const [resRes, orgRes] = await Promise.all([
        api.get('/resources').catch(() => ({ data: [] })),
        api.get('/partner-organizations').catch(() => ({ data: [] }))
      ]);
      setResources(resRes.data || []);
      setOrganizations(orgRes.data || []);
    } catch {}
  };

  useEffect(() => { load(); }, []);

  // Filter resources
  const filtered = resources.filter(r => {
    const matchesCategory = filter === 'all' || r.category === filter;
    const matchesSearch = !search || 
      r.title?.toLowerCase().includes(search.toLowerCase()) ||
      r.description?.toLowerCase().includes(search.toLowerCase());
    const matchesLocation = !locationFilter || 
      r.location?.toLowerCase().includes(locationFilter.toLowerCase());
    return matchesCategory && matchesSearch && matchesLocation;
  });

  // Filter organizations
  const filteredOrgs = organizations.filter(o => {
    const matchesCategory = filter === 'all' || o.category === filter;
    const matchesSearch = !search || 
      o.name?.toLowerCase().includes(search.toLowerCase()) ||
      o.description?.toLowerCase().includes(search.toLowerCase());
    const matchesLocation = !locationFilter || 
      o.service_area?.toLowerCase().includes(locationFilter.toLowerCase()) ||
      o.address?.toLowerCase().includes(locationFilter.toLowerCase());
    return matchesCategory && matchesSearch && matchesLocation;
  });

  const handleCreate = async () => {
    if (!form.title) return toast.error('Title is required');
    try {
      await api.post('/resources', form);
      toast.success('Resource added!');
      setOpen(false);
      setForm({ title: '', category: 'recovery', description: '', url: '', location: '', phone: '', eligibility: '' });
      load();
    } catch { toast.error('Failed to add resource'); }
  };

  const toggleSave = (id) => {
    setSavedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    toast.success(savedIds.has(id) ? 'Removed from saved' : 'Saved!');
  };

  // Get unique locations for filter suggestions
  const locations = [...new Set([
    ...resources.map(r => r.location).filter(Boolean),
    ...organizations.map(o => o.service_area).filter(Boolean)
  ])].slice(0, 10);

  return (
    <div className="space-y-6" data-testid="resources-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Resource Directory</h1>
          <p className="text-muted-foreground text-sm mt-1">Find resources, services, and community support</p>
        </div>
        {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-2" data-testid="add-resource-button">
                <Plus size={16} /> Add Resource
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl max-w-lg">
              <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>Add Resource</DialogTitle></DialogHeader>
              <div className="space-y-4 pt-2 max-h-[70vh] overflow-y-auto">
                <div className="space-y-1.5">
                  <Label>Title *</Label>
                  <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Resource name" data-testid="resource-title-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(c => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Description</Label>
                  <DictationInput value={form.description} onChange={(val) => setForm({...form, description: val})} rows={3} placeholder="Describe this resource..." showCleanup={false} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Location</Label>
                    <Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} placeholder="Area or address" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Phone</Label>
                    <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="(555) 123-4567" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Website (optional)</Label>
                  <Input value={form.url} onChange={e => setForm({...form, url: e.target.value})} placeholder="https://..." />
                </div>
                <div className="space-y-1.5">
                  <Label>Eligibility Requirements</Label>
                  <Input value={form.eligibility} onChange={e => setForm({...form, eligibility: e.target.value})} placeholder="Who can access this resource?" />
                </div>
                <Button onClick={handleCreate} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="create-resource-button">
                  Add Resource
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Search and Filters */}
      <Card className="border-0 shadow-sm rounded-2xl">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input 
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search resources..."
                className="pl-9 rounded-full"
                data-testid="resource-search"
              />
            </div>
            <div className="flex gap-2">
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-40 rounded-full" data-testid="resource-filter">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {CATEGORIES.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input 
                  value={locationFilter}
                  onChange={e => setLocationFilter(e.target.value)}
                  placeholder="Location..."
                  className="pl-9 w-32 sm:w-40 rounded-full"
                  data-testid="location-filter"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Category Quick Filters */}
      <div className="flex flex-wrap gap-2">
        <Button 
          variant={filter === 'all' ? 'default' : 'outline'} 
          size="sm" 
          className="rounded-full"
          onClick={() => setFilter('all')}
        >
          All
        </Button>
        {CATEGORIES.slice(0, 8).map(c => {
          const Icon = c.icon;
          return (
            <Button 
              key={c.value}
              variant={filter === c.value ? 'default' : 'outline'} 
              size="sm" 
              className={`rounded-full gap-1 ${filter === c.value ? '' : c.color}`}
              onClick={() => setFilter(c.value)}
            >
              <Icon size={14} /> {c.label}
            </Button>
          );
        })}
      </div>

      {/* Tabs for Resources vs Organizations */}
      <div className="flex gap-2 border-b">
        <button 
          onClick={() => setActiveTab('resources')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'resources' ? 'border-teal-600 text-teal-600' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
        >
          Resources ({filtered.length})
        </button>
        <button 
          onClick={() => setActiveTab('organizations')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'organizations' ? 'border-teal-600 text-teal-600' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
        >
          Organizations ({filteredOrgs.length})
        </button>
      </div>

      {/* Resources Grid */}
      {activeTab === 'resources' && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.length > 0 ? filtered.map(r => {
            const config = getCategoryConfig(r.category);
            const Icon = config.icon;
            const isSaved = savedIds.has(r.id);
            return (
              <Card key={r.id} className="border-0 shadow-sm rounded-2xl hover:shadow-md transition-shadow group">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${config.color.split(' ')[0]}`}>
                      <Icon className={`w-5 h-5 ${config.color.split(' ')[1]}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-medium text-sm">{r.title}</p>
                        <button onClick={() => toggleSave(r.id)} className="opacity-0 group-hover:opacity-100 transition-opacity">
                          {isSaved ? (
                            <Star className="w-4 h-4 text-amber-500 fill-current" />
                          ) : (
                            <Bookmark className="w-4 h-4 text-muted-foreground hover:text-teal-600" />
                          )}
                        </button>
                      </div>
                      <Badge className={config.color} variant="secondary">{config.label}</Badge>
                      {r.description && <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{r.description}</p>}
                      <div className="flex flex-wrap gap-2 mt-2 text-xs text-muted-foreground">
                        {r.location && (
                          <span className="flex items-center gap-1"><MapPin size={10} /> {r.location}</span>
                        )}
                        {r.phone && (
                          <span className="flex items-center gap-1"><Phone size={10} /> {r.phone}</span>
                        )}
                      </div>
                      {r.url && (
                        <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-xs text-teal-600 hover:underline flex items-center gap-1 mt-2">
                          <ExternalLink size={12} /> Visit Website
                        </a>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          }) : (
            <div className="col-span-full text-center py-12">
              <BookOpen className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No resources found matching your criteria.</p>
              {(search || filter !== 'all' || locationFilter) && (
                <Button variant="link" onClick={() => { setSearch(''); setFilter('all'); setLocationFilter(''); }}>
                  Clear filters
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Organizations Grid */}
      {activeTab === 'organizations' && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filteredOrgs.length > 0 ? filteredOrgs.map(o => {
            const config = getCategoryConfig(o.category);
            const Icon = config.icon;
            return (
              <Card key={o.id} className="border-0 shadow-sm rounded-2xl hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${config.color.split(' ')[0]}`}>
                      <Icon className={`w-5 h-5 ${config.color.split(' ')[1]}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm">{o.name}</p>
                      <Badge className={config.color} variant="secondary">{config.label}</Badge>
                      {o.verified && <Badge className="ml-1 bg-emerald-50 text-emerald-700">Verified</Badge>}
                      {o.description && <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{o.description}</p>}
                      <div className="flex flex-wrap gap-2 mt-2 text-xs text-muted-foreground">
                        {o.service_area && (
                          <span className="flex items-center gap-1"><MapPin size={10} /> {o.service_area}</span>
                        )}
                        {o.phone && (
                          <span className="flex items-center gap-1"><Phone size={10} /> {o.phone}</span>
                        )}
                      </div>
                      {o.website && (
                        <a href={o.website} target="_blank" rel="noopener noreferrer" className="text-xs text-teal-600 hover:underline flex items-center gap-1 mt-2">
                          <ExternalLink size={12} /> Visit Website
                        </a>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          }) : (
            <div className="col-span-full text-center py-12">
              <Users className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No organizations found matching your criteria.</p>
            </div>
          )}
        </div>
      )}

      {/* Crisis Resources Banner */}
      <Card className="border-0 shadow-sm rounded-2xl bg-gradient-to-r from-red-50 to-orange-50">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center shrink-0">
              <Phone className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="font-semibold text-red-900">Crisis Support Available 24/7</p>
              <p className="text-sm text-red-700">988 Suicide & Crisis Lifeline | National Domestic Violence Hotline: 1-800-799-7233</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
